import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { AlertsService } from 'src/app/demo/service/alerts.service';
import {
  CascadeLevel,
  CascadeNode,
  SelectCascadeNodeEvent
} from 'src/app/demo/interfaces/columnas-cascada.interface';

/**
  * @author Ministerio de cultura
  * @createdate 2026-09-02
  * @description Componente reciclable que renderiza un tablero de columnas en cascada (estilo "cadena de valor"):
  * cada nivel es una columna cuyos ítems, al seleccionarse, despliegan los ítems hijos en la columna siguiente.
  *
  * El componente es COMPLETAMENTE PRESENTACIONAL: no abre diálogos ni hace llamadas API.
  * Todas las acciones (crear, editar, ver, eliminar, botones opcionales) se emiten a través de
  * `onOptionalButtonClick` hacia el componente padre, quien es responsable de manejar la lógica de negocio.
  *
  * El padre entrega `levels`/`nodes` y reacciona a:
  * - `onSelectNode`: cuando se selecciona un nodo
  * - `onOptionalButtonClick`: cuando se hace clic en cualquier botón (crear, editar, ver, eliminar, etc.)
  */
@Component({
  selector: 'app-columnas-cascada',
  templateUrl: './columnas-cascada.component.html',
  styleUrl: './columnas-cascada.component.scss',
})
export class ColumnasCascadaComponent implements OnChanges, AfterViewInit {
  @ViewChild('cascadaBoard', { static: false }) cascadaBoardRef!: ElementRef<HTMLDivElement>;
  @Input() levels: CascadeLevel[] = [];
  @Input() nodes: CascadeNode[] = [];
  @Input() selection: (string | number | null)[] = [];
  /**
    * Id del padre "externo" del nivel 0, cuando el tablero no arranca en un nivel raíz real sino en un
    * sub-árbol de algo ya seleccionado fuera de este tablero (ej. un segundo `app-columnas-cascada` con el
    * presupuesto de una actividad puntual). Se usa únicamente al crear un nodo en el nivel 0; si el nivel 0 sí
    * es la raíz real (sin padre), se deja en `null` (valor por defecto).
    */
  @Input() rootParentId: string | number | null = null;
  @Input() showBreadcrumbs: boolean = true;
  @Input() textButtonSubmit: string = 'Guardar';
  @Input() emptyBoardMessage: string = 'Configure al menos un nivel para mostrar el tablero.';
  /**
    * Plantilla opcional para agregar botones de acción propios del consumidor junto a los de editar/ver/eliminar
    * de cada tarjeta. Se le pasa como contexto `$implicit` (el `CascadeNode` de la fila) y `level` (su índice),
    * ej.: `<ng-template #tpl let-node let-lvl="level"><app-boton-svg (click)="algo(node)" /></ng-template>`.
    * Necesario porque el contenido proyectado por `<ng-content>` se compila en el contexto del padre y no puede
    * acceder a `node`/`lvl` del `*ngFor` interno del tablero.
    */
  @Input() extraItemActionTemplate: TemplateRef<any> | null = null;

  @Output() selectionChange: EventEmitter<(string | number | null)[]> = new EventEmitter<(string | number | null)[]>();
  @Output() onSelectNode: EventEmitter<SelectCascadeNodeEvent> = new EventEmitter<SelectCascadeNodeEvent>();

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-09
    * @description Emite evento cuando el usuario hace clic en uno de los 4 botones opcionales
    * (configuracion, indicador-informacion, enviar, enviar-todo).
    */
  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-09
    * @description Emite evento unificado para TODOS los botones (predeterminados + opcionales).
    * Esto centraliza la gestión de acciones en el componente padre:
    * - Botones predeterminados: 'crear', 'editar', 'ver', 'eliminar'
    * - Botones opcionales: 'configuracion', 'indicador-informacion', 'enviar', 'enviar-todo'
    */
  @Output() onOptionalButtonClick: EventEmitter<{
    buttonName: 'crear' | 'editar' | 'ver' | 'eliminar' | 'configuracion' | 'indicador-informacion' | 'enviar' | 'enviar-todo';
    level: number;
    node: CascadeNode;
  }> = new EventEmitter<{
    buttonName: 'crear' | 'editar' | 'ver' | 'eliminar' | 'configuracion' | 'indicador-informacion' | 'enviar' | 'enviar-todo';
    level: number;
    node: CascadeNode;
  }>();

  /** Selección vigente: un id (o `null`) por cada nivel del tablero. Se sincroniza con el `@Input() selection`. */
  selectionState: (string | number | null)[] = [];

  /** Estado de los filtros de búsqueda por nivel: un string (o vacío) por cada columna. */
  filterState: string[] = [];

  /** Estados de navegación horizontal del tablero (scroll) */
  canScrollLeft: boolean = false;
  canScrollRight: boolean = false;
  private readonly COLUMN_WIDTH = 320; // Ancho de cada columna en px
  private readonly COLUMN_GAP = 16; // Gap entre columnas en px
  private scrollCheckTimer: any = null;

  /** Paleta de colores por defecto para los niveles que no definen `color`, tomada de la paleta del proyecto. */
  private readonly defaultLevelColors: string[] = [
    '#4B3C8C', // morado / purpura-indigo
    '#1A74CE', // azul-real
    '#08C2B3', // turquesa
    '#728B2D', // verde
    '#FBAF1F', // amarillo-mostaza
    '#F5661B', // naranja
    '#F51B3D', // rojo-fucsia
    '#3E8F69', // verde-menta-oscuro
    '#8171BF', // purpura-grisaceo
  ];

  constructor(
    private alertService: AlertsService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['levels']) this.syncSelectionLength();

    if (changes['selection'] && this.selection && !this.arraysAreEqual(this.selection, this.selectionState)) {
      this.selectionState = [...this.selection];
      this.syncSelectionLength();
    }

    if (changes['nodes']) {
      this.pruneInvalidSelection();
      // Verificar estado de scroll después de que cambien los nodos
      this.scheduleScrollCheck();
    }
  }

  ngAfterViewInit(): void {
    this.scheduleScrollCheck();
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-02
    * @description Retorna los nodos que se deben listar en la columna de un nivel: los del nivel raíz (0),
    * o los hijos del nodo seleccionado en el nivel anterior. Aplica el filtro de búsqueda si existe.
    */
  nodesOfLevel(level: number): CascadeNode[] {
    let filtered: CascadeNode[] = [];

    if (level === 0) {
      filtered = this.nodes.filter(node => node.level === 0);
    } else {
      const parentId = this.selectionState[level - 1];
      if (parentId === null || parentId === undefined) return [];
      filtered = this.nodes.filter(node => node.level === level && node.parentId === parentId);
    }

    // Aplicar filtro de búsqueda si existe
    const searchTerm = (this.filterState[level] || '').toLowerCase().trim();
    if (searchTerm) {
      filtered = filtered.filter(node => {
        const nodeTitle = this.titleOfNode(node, level).toLowerCase();
        const nodeSummary = (this.summaryOfNode(node, level) || '').toLowerCase();
        return nodeTitle.includes(searchTerm) || nodeSummary.includes(searchTerm);
      });
    }

    return filtered;
  }

  /** Indica si la columna de un nivel puede mostrar contenido: el nivel raíz siempre, los demás solo si su padre ya fue seleccionado. */
  parentSelected(level: number): boolean {
    return level === 0 || (this.selectionState[level - 1] !== null && this.selectionState[level - 1] !== undefined);
  }

  nodeById(id: string | number | null): CascadeNode | null {
    if (id === null || id === undefined) return null;
    return this.nodes.find(node => node.id === id) || null;
  }

  isSelected(level: number, id: string | number): boolean {
    return this.selectionState[level] === id;
  }

  hasSelection(): boolean {
    return this.selectionState.some(id => id !== null && id !== undefined);
  }

  colorOfLevel(level: number): string {
    return this.levels[level]?.color || this.defaultLevelColors[level % this.defaultLevelColors.length];
  }

  titleOfNode(node: CascadeNode | null, level: number): string {
    if (!node) return '';
    const key = this.levels[level]?.titleField || 'nombre';
    return node.data?.[key] ?? '';
  }

  summaryOfNode(node: CascadeNode, level: number): string {
    const keys = this.levels[level]?.summaryFields || [];
    return keys
      .map(key => node.data?.[key])
      .filter(value => value !== null && value !== undefined && value !== '')
      .join(' · ');
  }

  namePluralOfLevel(level: number): string {
    const cascadeLevel = this.levels[level];
    return cascadeLevel?.namePlural || `${cascadeLevel?.name}s`;
  }

  emptyColumnMessage(level: number): string {
    if (!this.parentSelected(level)) {
      const previousLevelName = this.levels[level - 1]?.name?.toLowerCase() || 'elemento';
      return `Selecciona un ${previousLevelName} en la columna anterior.`;
    }
    return `Aún no hay ${this.namePluralOfLevel(level).toLowerCase()}. Usa el botón "+" para crear el primero.`;
  }

  canCreate(level: number): boolean {
    return this.levels[level]?.allowCreate !== false;
  }

  canEdit(level: number): boolean {
    return this.levels[level]?.allowEdit !== false;
  }

  canDelete(level: number): boolean {
    return this.levels[level]?.allowDelete !== false;
  }

  canView(level: number): boolean {
    return this.levels[level]?.allowView !== false;
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-02
    * @description Retorna una versión bindeada de selectNode para usar en plantillas proyectadas.
    * Esto evita problemas de contexto 'this' cuando se pasa la función al contexto de ng-template.
    */
  get selectNodeFn() {
    return this.selectNode.bind(this);
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-09
    * @description Valida si un botón opcional debe mostrarse en un nivel específico.
    */
  canShowButton(
    level: number,
    buttonName: 'configuracion' | 'indicador-informacion' | 'enviar' | 'enviar-todo'
  ): boolean {
    const allowMap: Record<string, keyof CascadeLevel> = {
      'configuracion': 'allowConfiguracion',
      'indicador-informacion': 'allowIndicadorInfo',
      'enviar': 'allowEnviar',
      'enviar-todo': 'allowEnviarTodo'
    };

    const levelConfig = this.levels[level];
    if (!levelConfig) return false;

    const allowKey = allowMap[buttonName];
    return levelConfig[allowKey] !== false;
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-09
    * @description Crea un nodo placeholder para la acción de "crear" (donde no hay nodo existente aún).
    * Se usa para pasar información al evento onOptionalButtonClick cuando se abre el diálogo de creación.
    */
  createPlaceholderNode(level: number): CascadeNode {
    const parentId = level === 0 ? this.rootParentId : this.selectionState[level - 1];
    return {
      id: -1, // ID temporal indicando que es un placeholder
      level,
      parentId: parentId ?? null,
      data: {}
    };
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-13
    * @description Prefijo estable para los `id` de DOM de un nivel: usa `idPrefix` si el padre lo definió, o
    * un slug de `name` en caso contrario (ver `idPrefix` en `CascadeLevel`).
    */
  private idPrefixFor(level: number): string {
    const levelConfig = this.levels[level];
    return levelConfig?.idPrefix || this.slugify(levelConfig?.name || `nivel-${level}`);
  }

  /** Normaliza un texto a slug (minúsculas, sin tildes, separado por guiones) para usarlo en un `id` de DOM. */
  private slugify(text: string): string {
    return text
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-13
    * @description `id` de DOM de la tarjeta de un nodo (ej. `item-proyecto-5`), para que las pruebas
    * funcionales puedan seleccionarla sin ambigüedad entre columnas/tableros.
    */
  itemId(level: number, node: CascadeNode): string {
    return `item-${this.idPrefixFor(level)}-${node.id}`;
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-13
    * @description `id` de DOM de un botón de acción del tablero: el botón "+" de la columna (sin `node`, ej.
    * `crear-proyecto`) o el botón de una tarjeta puntual (con `node`, ej. `editar-proyecto-5`).
    */
  actionId(action: string, level: number, node?: CascadeNode): string {
    const prefix = this.idPrefixFor(level);
    return node ? `${action}-${prefix}-${node.id}` : `${action}-${prefix}`;
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-09
    * @description Emite evento unificado para TODOS los botones (predeterminados + opcionales).
    * Permite que el componente padre centralice la gestión de todas las acciones.
    */
  onOptionalButtonClickHandler(
    buttonName: 'crear' | 'editar' | 'ver' | 'eliminar' | 'configuracion' | 'indicador-informacion' | 'enviar' | 'enviar-todo',
    level: number,
    node: CascadeNode
  ): void {
    this.onOptionalButtonClick.emit({ buttonName, level, node });
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-02
    * @description Selecciona un nodo en un nivel del tablero y limpia la selección de los niveles siguientes,
    * dado que sus hijos dependían del nodo anterior. Emite `selectionChange` y `onSelectNode`.
    */
  selectNode(level: number, id: string | number): void {
    this.selectionState[level] = id;
    for (let i = level + 1; i < this.selectionState.length; i++) this.selectionState[i] = null;

    // Limpiar filtros de las columnas siguientes al cambiar de selección
    for (let i = level + 1; i < this.filterState.length; i++) this.filterState[i] = '';

    this.selectionChange.emit([...this.selectionState]);
    this.onSelectNode.emit({ level, nodeId: id, node: this.nodeById(id) });
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-04
    * @description Maneja el cambio en el valor del filtro de búsqueda para un nivel.
    * Actualiza `filterState` y redibuja la lista sin cambiar la selección vigente.
    */
  onFilterChange(level: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.filterState[level] = input.value;
  }

  private syncSelectionLength(): void {
    const length = this.levels.length;
    if (this.selectionState.length > length) this.selectionState = this.selectionState.slice(0, length);
    while (this.selectionState.length < length) this.selectionState.push(null);

    // Sincronizar también el estado de los filtros
    if (this.filterState.length > length) this.filterState = this.filterState.slice(0, length);
    while (this.filterState.length < length) this.filterState.push('');
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-02
    * @description Cuando cambian los `nodes` (ej: tras una eliminación o edición persistida por el padre), limpia
    * de la selección cualquier id que ya no exista en su columna, junto con los niveles siguientes que dependían de él.
    */
  private pruneInvalidSelection(): void {
    let changed = false;

    for (let level = 0; level < this.selectionState.length; level++) {
      const id = this.selectionState[level];
      if (id === null || id === undefined) continue;

      const stillExists = this.nodesOfLevel(level).some(node => node.id === id);
      if (!stillExists) {
        for (let i = level; i < this.selectionState.length; i++) {
          if (this.selectionState[i] !== null) changed = true;
          this.selectionState[i] = null;
        }
        break;
      }
    }

    if (changed) this.selectionChange.emit([...this.selectionState]);
  }

  private arraysAreEqual(a: (string | number | null)[], b: (string | number | null)[]): boolean {
    return a.length === b.length && a.every((value, index) => value === b[index]);
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-07
    * @description Desplaza el tablero hacia la izquierda por el ancho de una columna.
    */
  scrollLeft(): void {
    if (!this.cascadaBoardRef) return;
    const board = this.cascadaBoardRef.nativeElement.parentElement;
    if (!board) return;

    const scrollDistance = this.COLUMN_WIDTH + this.COLUMN_GAP;
    board.scrollBy({ left: -scrollDistance, behavior: 'smooth' });
    this.scheduleScrollCheck();
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-07
    * @description Desplaza el tablero hacia la derecha por el ancho de una columna.
    */
  scrollRight(): void {
    if (!this.cascadaBoardRef) return;
    const board = this.cascadaBoardRef.nativeElement.parentElement;
    if (!board) return;

    const scrollDistance = this.COLUMN_WIDTH + this.COLUMN_GAP;
    board.scrollBy({ left: scrollDistance, behavior: 'smooth' });
    this.scheduleScrollCheck();
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-07
    * @description Verifica si el tablero puede desplazarse hacia la izquierda y derecha,
    * y actualiza los estados `canScrollLeft` y `canScrollRight`.
    */
  private updateScrollState(): void {
    if (!this.cascadaBoardRef) return;
    const board = this.cascadaBoardRef.nativeElement.parentElement;
    if (!board) return;

    this.canScrollLeft = board.scrollLeft > 0;
    this.canScrollRight = board.scrollLeft < board.scrollWidth - board.clientWidth - 5; // Margen de 5px
  }

  /**
    * @author Ministerio de cultura
    * @createdate 2026-09-07
    * @description Programa una verificación del estado de scroll. Se ejecuta con un pequeño delay
    * para permitir que el DOM se actualice completamente después de cambios en los nodos.
    */
  private scheduleScrollCheck(): void {
    if (this.scrollCheckTimer) clearTimeout(this.scrollCheckTimer);
    this.scrollCheckTimer = setTimeout(() => this.updateScrollState(), 50);
  }
}
